import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ShieldX, Camera, Upload, CheckCircle2, QrCode, Users, Baby, RefreshCw } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { DigitalCard } from "../components/DigitalCard";
import { TransferAccountsCard } from "../components/TransferAccountsCard";
import { api, apiError, money } from "../lib/api";
import { toast } from "sonner";

const MAX_FILE_MB = 5;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const AccionSeguraPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [data, setData] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [uploadErr, setUploadErr] = useState(null);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [error, setError] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.get(`/public/link/${token}`, { params: { _ts: Date.now() }, headers: { "Cache-Control": "no-cache" } })
      .then((r) => { setData(r.data); setLastUpdate(new Date()); })
      .catch((e) => { if (e?.response?.status === 404) setInvalid(true); else setError(true); })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pickFile = (file, setter, previewSetter) => {
    if (!file) return;
    if (setter === setPhoto && !IMAGE_TYPES.includes(file.type)) {
      toast.error("Formato no permitido. Usa JPG, PNG o WEBP.");
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`El archivo excede ${MAX_FILE_MB} MB.`);
      return;
    }
    setter(file);
    if (previewSetter) previewSetter(URL.createObjectURL(file));
  };

  const submitPhoto = async () => {
    if (!photo || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo", photo);
      await api.post(`/public/link/${token}/child-photo`, fd);
      setDone("photo");
    } catch (err) {
      toast.error(apiError(err, "No se pudo guardar la fotografía."));
    } finally {
      setBusy(false);
    }
  };

  const submitReceipt = async () => {
    if (!queue.length || busy) return;
    setBusy(true);
    setUploadErr(null);
    setUploadMsg(null);
    const failed = [];
    let successCount = 0;
    for (const file of queue) {
      try {
        const fd = new FormData();
        fd.append("receipt", file);
        await api.post(`/public/link/${token}/receipt`, fd);
        successCount += 1;
      } catch (err) {
        failed.push({ name: file.name, msg: apiError(err, "No se pudo subir.") });
      }
    }
    try {
      const { data: refreshed } = await api.get(`/public/link/${token}`, { params: { _ts: Date.now() } });
      setData(refreshed);
      setLastUpdate(new Date());
    } catch { /* best-effort */ }
    setQueue(failed.length ? queue.filter((f) => failed.some((x) => x.name === f.name)) : []);
    if (successCount > 0 && failed.length === 0) {
      setDone("receipt");
      setUploadMsg(successCount === 1 ? "Comprobante recibido." : `${successCount} comprobantes recibidos.`);
    } else if (successCount > 0 && failed.length > 0) {
      setUploadErr(`Se subieron ${successCount} archivo(s). Falló: ${failed.map((f) => `${f.name} (${f.msg})`).join("; ")}`);
    } else {
      toast.error(failed[0]?.msg || "No se pudo subir el comprobante.");
      setUploadErr(failed[0]?.msg || "No se pudo subir el comprobante.");
    }
    setBusy(false);
  };

  const addReceiptFiles = (files) => {
    setUploadErr(null); setUploadMsg(null);
    const incoming = Array.from(files || []);
    if (!incoming.length) return;
    const next = [...queue];
    for (const file of incoming) {
      if (!["image/jpeg", "image/png", "application/pdf"].includes(file.type)) {
        setUploadErr(`Formato no permitido en ${file.name}. Usa JPG, PNG o PDF.`);
        continue;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        setUploadErr(`${file.name} excede ${MAX_FILE_MB} MB.`);
        continue;
      }
      next.push(file);
    }
    setQueue(next);
  };

  const removeFromQueue = (idx) => setQueue(queue.filter((_, i) => i !== idx));

  return (
    <PublicLayout>
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-hz-navy" data-testid="secure-link-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Verificando tu registro y accesos…
          </div>
        ) : invalid ? (
          <div className="rounded-2xl border border-hz-gray/40 bg-white p-8 text-center" data-testid="secure-link-invalid">
            <ShieldX className="mx-auto h-12 w-12 text-hz-gray" />
            <h1 className="mt-4 text-xl font-bold text-hz-navy">Enlace no disponible</h1>
            <p className="mt-2 text-sm text-hz-ink/70">Este enlace es inválido, ya fue utilizado o expiró. Si necesitas ayuda, contacta al equipo organizador o consulta tu registro con tu folio.</p>
            <Link to="/consulta-registro" data-testid="secure-link-goto-consulta">
              <Button className="mt-6 bg-hz-navy font-bold text-white">Consultar mi registro</Button>
            </Link>
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-hz-gray/40 bg-white p-8 text-center" data-testid="secure-link-error">
            <RefreshCw className="mx-auto h-12 w-12 text-hz-gray" />
            <h1 className="mt-4 text-xl font-bold text-hz-navy">No pudimos actualizar el estado de tu registro</h1>
            <p className="mt-2 text-sm text-hz-ink/70">Intenta nuevamente. Tu información de acceso solo se muestra tras confirmar el estado vigente.</p>
            <Button onClick={load} className="mt-6 bg-hz-navy font-bold text-white" data-testid="secure-retry-btn">Actualizar ahora</Button>
          </div>
        ) : done === "photo" ? (
          <div className="rounded-2xl border border-hz-success/30 bg-white p-8 text-center" data-testid="secure-photo-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-hz-success" />
            <h1 className="mt-4 text-xl font-bold text-hz-navy">Fotografía recibida</h1>
            <p className="mt-2 text-sm text-hz-ink/70">El equipo organizador validará la fotografía. Este enlace ya no puede usarse de nuevo.</p>
          </div>
        ) : data.purpose === "foto_infantil" ? (
          <div className="space-y-6" data-testid="secure-child-photo">
            <div className="rounded-2xl border border-hz-gray/40 bg-white p-8 text-center">
              <Baby className="mx-auto h-10 w-10 text-hz-cyan" />
              <h1 className="mt-3 text-xl font-bold text-hz-navy">Completar fotografía para cuidado infantil</h1>
              <p className="mt-2 text-sm text-hz-ink/70">
                Registro {data.group_folio}. Para habilitar el acceso al área de cuidado infantil necesitamos una fotografía reciente y clara del menor. La foto se usa exclusivamente para identificación y entrega segura durante el evento.
              </p>
            </div>
            <div className="rounded-2xl border border-hz-gray/40 bg-white p-6">
              {photoPreview ? (
                <div className="flex items-center gap-4">
                  <img src={photoPreview} alt="Vista previa" className="h-24 w-24 rounded-2xl border border-hz-gray/50 object-cover" data-testid="secure-photo-preview" />
                  <Button variant="outline" size="sm" onClick={() => { setPhoto(null); setPhotoPreview(null); }} data-testid="secure-photo-remove" className="font-semibold text-hz-error">Quitar</Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-hz-gray/60 px-4 py-6 text-sm font-semibold text-hz-navy hover:border-hz-cyan">
                    <Camera className="h-5 w-5" /> Tomar foto
                    <input type="file" accept="image/jpeg,image/png,image/webp" capture="user" className="hidden" data-testid="secure-photo-camera" onChange={(e) => pickFile(e.target.files?.[0], setPhoto, setPhotoPreview)} />
                  </label>
                  <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-hz-gray/60 px-4 py-6 text-sm font-semibold text-hz-navy hover:border-hz-cyan">
                    <Upload className="h-5 w-5" /> Subir imagen
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="secure-photo-upload" onChange={(e) => pickFile(e.target.files?.[0], setPhoto, setPhotoPreview)} />
                  </label>
                </div>
              )}
              <Button onClick={submitPhoto} disabled={!photo || busy} data-testid="secure-photo-submit" className="mt-4 w-full bg-hz-cyan font-bold text-white hover:bg-hz-blue disabled:opacity-60">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Enviar fotografía
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6" data-testid="secure-consulta">
            <div className="rounded-2xl border border-hz-gray/40 bg-white p-6" data-testid="secure-group-info">
              <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">Consulta segura de registro</p>
              <h1 className="mt-1 text-xl font-bold text-hz-navy">{data.group.group_name || "Registro"} · {data.group.group_folio}</h1>
              <p className="mt-1 text-sm text-hz-ink/70">Contacto: {data.group.primary_contact_name}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
                <span className="rounded-full bg-hz-navy/5 px-3 py-1 text-hz-navy">{data.group.total_adults} adulto(s)</span>
                <span className="rounded-full bg-hz-navy/5 px-3 py-1 text-hz-navy" data-testid="secure-payment-status">Pago: {{ cancelled: "Registro cancelado", pending: "Pendiente de pago", receipt_uploaded: "En revisión", approved: "Confirmado", rejected: "Rechazado", correction_requested: "Corrección solicitada", waived: "Cortesía", imported_confirmed: "Confirmado" }[data.group.payment_mode] || data.group.payment_mode}</span>
                {data.group.total_amount != null && (
                  <span className="rounded-full bg-hz-navy/5 px-3 py-1 text-hz-navy" data-testid="secure-total">Total a pagar: {money(data.group.total_amount)} MXN</span>
                )}
              </div>
              {data.group.transfer_accounts != null && (
                <div className="mt-4" data-testid="secure-transfer-accounts">
                  <TransferAccountsCard accounts={data.group.transfer_accounts} folio={data.group.group_folio} testId="secure-transfer-card" />
                </div>
              )}
              <div className="mt-4 divide-y divide-hz-gray/30">
                {data.members.map((m) => (
                  <div key={m.folio} className="flex items-center justify-between py-2.5 text-sm" data-testid={`secure-member-${m.folio}`}>
                    <span className="font-semibold text-hz-ink">{m.full_name}</span>
                    <span className="font-extrabold tracking-wider text-hz-navy">{m.folio}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-hz-gray/30 pt-3">
                <p className="text-xs text-hz-ink/60" data-testid="secure-realtime-note">
                  Información actualizada en tiempo real.{lastUpdate ? ` Última actualización: ${lastUpdate.toLocaleTimeString("es-MX")}.` : ""}
                </p>
                <Button variant="outline" size="sm" onClick={load} data-testid="secure-refresh-btn" className="font-semibold">
                  <RefreshCw className="mr-1 h-3.5 w-3.5" /> Actualizar ahora
                </Button>
              </div>
            </div>

            {data.children?.length > 0 && (
              <div className="rounded-xl border border-hz-gray/40 bg-white px-4 py-3 text-sm" data-testid="secure-children">
                <p className="font-bold text-hz-navy">Niños registrados ({data.children.length})</p>
                <ul className="mt-1.5 space-y-1">
                  {data.children.map((c) => (
                    <li key={c.id} className="text-hz-ink/80" data-testid={`secure-child-${c.id}`}>
                      {c.full_name}{c.care_required ? " · Cuidado infantil solicitado" : ""}{c.photo_status === "pending" ? " · Fotografía pendiente" : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.capabilities.can_view_qr && (
              <div className="space-y-4" data-testid="secure-qr-cards">
                <p className="flex items-center gap-2 text-sm font-bold text-hz-navy"><QrCode className="h-4 w-4 text-hz-cyan" /> Tarjetas de acceso</p>
                {data.members.filter((m) => m.qr_token).map((m) => (
                  <DigitalCard key={m.folio} data={m} />
                ))}
              </div>
            )}

            {data.capabilities.can_upload_receipt && (
              <div className="rounded-2xl border-2 border-dashed border-hz-cyan/50 bg-hz-cyan/5 p-5" data-testid="secure-receipt-section">
                {/* Fase 2C — Carga posterior: muestra el saldo pendiente cuando aplica. */}
                {(data.group.payment_outstanding_amount_cents || 0) > 0 && (
                  <div className="mb-3 rounded-lg border-2 border-hz-warning/50 bg-hz-warning/10 px-3 py-2.5" data-testid="secure-outstanding-banner">
                    <p className="text-sm font-extrabold text-hz-warning">
                      Saldo pendiente: <span data-testid="secure-outstanding-amount">${(data.group.payment_outstanding_amount_cents / 100).toLocaleString("es-MX", {minimumFractionDigits: 2, maximumFractionDigits: 2})} MXN</span>
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-hz-ink">
                      {["approved", "waived", "imported_confirmed"].includes(data.group.payment_mode)
                        ? "Los accesos ya aprobados aparecen abajo. Sube el comprobante del pago restante para completar los accesos pendientes."
                        : "Para completar tus accesos, sube el comprobante del pago restante."}
                    </p>
                  </div>
                )}
                <p className="text-sm font-bold text-hz-navy">
                  {(data.group.payment_outstanding_amount_cents || 0) > 0 && ["approved", "waived", "imported_confirmed"].includes(data.group.payment_mode)
                    ? "Subir comprobante del saldo pendiente"
                    : data.group.payment_mode === "receipt_uploaded" ? "Agregar otro comprobante"
                    : data.group.payment_mode === "correction_requested" ? "Subir comprobante corregido"
                    : data.group.payment_mode === "rejected" ? "Subir nuevo comprobante" : "Subir comprobante de pago"}
                </p>
                <p className="mt-1 text-xs text-hz-ink/60">Puedes adjuntar varios comprobantes para respaldar tu pago. JPG, PNG o PDF — máximo {MAX_FILE_MB} MB por archivo.</p>
                <p className="mt-1 text-xs text-hz-ink/50">En algunos dispositivos, la cámara puede abrirse mediante el selector del sistema.</p>
                {["rejected", "correction_requested"].includes(data.group.payment_mode) && data.group.payment_review_notes && (
                  <p className="mt-2 rounded-lg border border-hz-warning/40 bg-hz-warning/5 px-3 py-2 text-xs font-medium text-hz-ink" data-testid="secure-review-notes">
                    Nota del revisor: {data.group.payment_review_notes}
                  </p>
                )}
                {(data.group.payment_receipts || []).length > 0 && (
                  <div className="mt-3 rounded-lg border border-hz-gray/40 bg-white" data-testid="secure-receipts-list">
                    <p className="border-b border-hz-gray/40 px-3 py-2 text-xs font-bold text-hz-navy">Comprobantes recibidos ({data.group.payment_receipts.length})</p>
                    <ul className="divide-y divide-hz-gray/30 text-xs">
                      {data.group.payment_receipts.map((r) => (
                        <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`secure-receipt-${r.version_number}`}>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-hz-ink">#{r.version_number} · {r.original_filename}</p>
                            <p className="text-[11px] text-hz-ink/60">{new Date(r.uploaded_at).toLocaleString("es-MX")}</p>
                          </div>
                          {r.download_url && (
                            <a href={r.download_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs font-bold text-hz-blue hover:underline" data-testid={`secure-receipt-view-${r.version_number}`}>Ver</a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <label className="cursor-pointer rounded-lg border border-hz-gray/50 bg-white px-3 py-2 text-center text-sm font-semibold text-hz-navy hover:border-hz-cyan">
                    Tomar foto
                    <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="secure-receipt-camera" onChange={(e) => { addReceiptFiles(e.target.files); e.target.value = ""; }} />
                  </label>
                  <label className="cursor-pointer rounded-lg border border-hz-gray/50 bg-white px-3 py-2 text-center text-sm font-semibold text-hz-navy hover:border-hz-cyan">
                    Subir archivos
                    <input type="file" multiple accept="image/jpeg,image/png,application/pdf" className="hidden" data-testid="secure-receipt-input" onChange={(e) => { addReceiptFiles(e.target.files); e.target.value = ""; }} />
                  </label>
                </div>
                {queue.length > 0 && (
                  <ul className="mt-3 space-y-1 rounded-lg border border-hz-gray/40 bg-white p-2 text-xs" data-testid="secure-queue">
                    {queue.map((f, idx) => (
                      <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2" data-testid={`secure-queue-item-${idx}`}>
                        <span className="truncate font-semibold text-hz-ink" title={f.name}>{f.name} <span className="font-normal text-hz-ink/50">({(f.size / 1024).toFixed(0)} KB)</span></span>
                        <button type="button" onClick={() => removeFromQueue(idx)} className="shrink-0 text-xs font-bold text-hz-error hover:underline" data-testid={`secure-queue-remove-${idx}`}>Quitar</button>
                      </li>
                    ))}
                  </ul>
                )}
                <Button onClick={submitReceipt} disabled={!queue.length || busy} data-testid="secure-receipt-submit" className="mt-3 w-full bg-hz-cyan font-bold text-white hover:bg-hz-blue disabled:opacity-60">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Enviar {queue.length > 1 ? `${queue.length} comprobantes` : "comprobante"}
                </Button>
                {uploadErr && <p className="mt-2 text-xs font-medium text-hz-error" role="alert">{uploadErr}</p>}
                {uploadMsg && <p className="mt-2 text-xs font-semibold text-hz-success">{uploadMsg}</p>}
              </div>
            )}

            {data.group.payment_mode === "receipt_uploaded" && (
              <div className="rounded-xl border border-hz-cyan/40 bg-hz-cyan/5 px-4 py-3 text-center text-sm font-semibold text-hz-blue" data-testid="secure-in-review">
                Comprobante recibido; está en revisión{data.group.payment_uploaded_at ? ` desde el ${new Date(data.group.payment_uploaded_at).toLocaleString("es-MX")}` : ""}.
              </div>
            )}
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default AccionSeguraPage;
